import { useCallback } from 'react';
import { toast } from 'react-toastify';

const useErrorHandler = () => {
  const handleError = useCallback((error, context = '') => {
    console.error(`Error in ${context}:`, error);
    
    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      // In a real app, you would send this to an error reporting service
      console.error('Production error logging:', {
        error: error.toString(),
        stack: error.stack,
        context,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      });
    }

    // Show user-friendly error message
    const errorMessage = error.message || 'An unexpected error occurred';
    toast.error(`${context ? `${context}: ` : ''}${errorMessage}`, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, []);

  const handleAsyncError = useCallback(async (asyncFunction, context = '') => {
    try {
      return await asyncFunction();
    } catch (error) {
      handleError(error, context);
      throw error; // Re-throw so calling code can handle if needed
    }
  }, [handleError]);

  const handlePromiseError = useCallback((promise, context = '') => {
    return promise.catch(error => {
      handleError(error, context);
      return null; // Return null instead of throwing
    });
  }, [handleError]);

  return {
    handleError,
    handleAsyncError,
    handlePromiseError
  };
};

export default useErrorHandler;
